import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { Observable, startWith, take } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { AdmOrgTypeaheadComponent } from 'src/app/shared/components/adm-org-typeahead/adm-org-typeahead.component';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { PriceRegulation, PriceRegulationScopeType, PriceRegulationType } from '../price-regulation';
import { Organization } from 'src/app/features/organization/organization-models';
import { TranslatePriceRegulationTypePipe } from 'src/app/shared/pipes/translate-price-regulation-type.pipe';
import { AdmPriceExInclVatComponent } from '../../price-policies/components/price-ex-incl-vat/price-ex-incl-vat.component';
import {
  CreatePriceRegulationRequest,
  EditDiscountPriceRegulationRequest,
  EditSupplementOverridePriceRegulationRequest,
  PriceRegulationService,
} from 'src/app/shared/services/price-regulation.service';
import { NoteCreationModalComponent } from 'src/app/shared/components/modals/price-policy-note-creation-modal/note-creation-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { supplementOverrideValidator } from 'src/app/shared/validators/supplement-override.validator';

interface OrganizationOption {
  id: number;
  name: string;
}

interface PriceRegulationDialogData {
  identifier?: string | null;
  organizationId?: number | null;
  organizationName?: string;
}

export interface PriceRegulationDialogCloseResult {
  saved: boolean;
  sortBy: 'created' | 'updated';
  identifier: string;
  priceRegulation?: PriceRegulation;
}

interface PriceRegulationsForm {
  name: FormControl<string>;
  scopeType: FormControl<PriceRegulationScopeType>;
  organization: FormControl<OrganizationOption | null>;
  type: FormControl<PriceRegulationType>;
  discountAmountInKrWithoutVat: FormControl<number | null>;

  overrideOkSupplement: FormControl<boolean>;
  okSupplementOverrideInKrWithoutVat: FormControl<number | null>;

  overrideOrganizationSupplement: FormControl<boolean>;
  organizationSupplementOverrideInKrWithoutVat: FormControl<number | null>;
}

@Component({
  selector: 'adm-price-regulations-create-edit-modal',
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButton,
    MatIcon,
    MatFormField,
    MatLabel,
    MatError,
    MatSuffix,
    MatInput,
    MatSelect,
    MatOption,
    MatRadioModule,
    AdmOrgTypeaheadComponent,
    CustomDropDownArrowDirective,
    TranslatePriceRegulationTypePipe,
    AdmPriceExInclVatComponent,
    MatSlideToggle,
  ],
  templateUrl: './price-regulations-create-edit-modal.component.html',
  styleUrl: './price-regulations-create-edit-modal.component.scss',
})
export class PriceRegulationsCreateEditModalComponent {
  private dialogRef = inject(MatDialogRef<PriceRegulationsCreateEditModalComponent>, { optional: true });
  private dialog = inject(MatDialog);
  private data = inject<PriceRegulationDialogData | null>(MAT_DIALOG_DATA, { optional: true });
  private priceRegulationService = inject(PriceRegulationService, { optional: false });

  public isEditMode = !!this.data?.identifier;

  public PriceRegulationScopeType = PriceRegulationScopeType;
  public PriceRegulationType = PriceRegulationType;

  public form = new FormGroup<PriceRegulationsForm>(
    {
      name: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(100), Validators.pattern(/.*\S.*/)],
      }),
      scopeType: new FormControl(PriceRegulationScopeType.Global, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      organization: new FormControl<OrganizationOption | null>(null),
      type: new FormControl(PriceRegulationType.Discount, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      discountAmountInKrWithoutVat: new FormControl<number | null>(null),

      overrideOkSupplement: new FormControl(false, { nonNullable: true }),
      okSupplementOverrideInKrWithoutVat: new FormControl<number | null>(null),

      overrideOrganizationSupplement: new FormControl(false, { nonNullable: true }),
      organizationSupplementOverrideInKrWithoutVat: new FormControl<number | null>(null),
    },
    { validators: supplementOverrideValidator(PriceRegulationType.SupplementOverride) }
  );

  public scopeType = toSignal(this.form.controls.scopeType.valueChanges.pipe(startWith(this.form.controls.scopeType.value)), {
    initialValue: this.form.controls.scopeType.value,
  });

  public selectedType = toSignal(this.form.controls.type.valueChanges.pipe(startWith(this.form.controls.type.value)), {
    initialValue: this.form.controls.type.value,
  });

  constructor() {
    this.form.controls.overrideOkSupplement.valueChanges.subscribe(enabled => {
      this.syncSupplementControl(this.form.controls.okSupplementOverrideInKrWithoutVat, enabled);
    });

    this.form.controls.overrideOrganizationSupplement.valueChanges.subscribe(enabled => {
      this.syncSupplementControl(this.form.controls.organizationSupplementOverrideInKrWithoutVat, enabled);
    });
    this.form.controls.scopeType.valueChanges.subscribe(scopeType => {
      this.syncScopeControl(scopeType, true);
    });

    // Keep organization field state aligned with initial scope value.
    this.syncScopeControl(this.form.controls.scopeType.value);

    this.form.controls.type.valueChanges.subscribe(() => {
      this.syncTypeControls();
    });

    this.syncTypeControls();

    if (!this.isEditMode && this.data?.organizationId) {
      this.form.patchValue(
        {
          scopeType: PriceRegulationScopeType.Organization,
          organization: {
            id: this.data.organizationId,
            name: this.data.organizationName ?? '',
          },
        },
        { emitEvent: true }
      );
      this.form.controls.scopeType.disable({ emitEvent: false });
      this.form.controls.organization.disable({ emitEvent: false });
    }

    if (this.data?.identifier) {
      this.priceRegulationService
        .getPriceRegulation(this.data.identifier)
        .pipe(take(1))
        .subscribe(priceRegulation => {
          this.patchFormFromPriceRegulation(priceRegulation);
          this.lockEditOnlyFields();
        });
    }
  }

  private lockEditOnlyFields(): void {
    this.form.controls.scopeType.disable({ emitEvent: false });
    this.form.controls.type.disable({ emitEvent: false });
    this.form.controls.organization.disable({ emitEvent: false });
  }

  private syncScopeControl(scopeType: PriceRegulationScopeType, resetOrganization: boolean = false): void {
    if (scopeType === PriceRegulationScopeType.Organization) {
      this.form.controls.organization.enable({ emitEvent: false });
      this.form.controls.organization.setValidators([Validators.required]);
      this.form.controls.organization.updateValueAndValidity({ emitEvent: false });
      return;
    }

    if (resetOrganization) {
      this.form.controls.organization.setValue(null, { emitEvent: false });
    }
    this.form.controls.organization.clearValidators();
    this.form.controls.organization.disable({ emitEvent: false });
    this.form.controls.organization.updateValueAndValidity({ emitEvent: false });
  }

  private patchFormFromPriceRegulation(priceRegulation: PriceRegulation): void {
    this.form.patchValue(
      {
        name: priceRegulation.name,
        scopeType: priceRegulation.scopeType,
        organization: priceRegulation.organizationId
          ? {
              id: priceRegulation.organizationId,
              name: priceRegulation.organizationName ?? '',
            }
          : null,
        type: priceRegulation.type,
        discountAmountInKrWithoutVat: priceRegulation.discountAmountInKrWithoutVat,
        okSupplementOverrideInKrWithoutVat: priceRegulation.okSupplementOverrideInKrWithoutVat,
        organizationSupplementOverrideInKrWithoutVat: priceRegulation.organizationSupplementOverrideInKrWithoutVat,
        overrideOkSupplement: priceRegulation.okSupplementOverrideInKrWithoutVat !== null,
        overrideOrganizationSupplement: priceRegulation.organizationSupplementOverrideInKrWithoutVat !== null,
      },
      { emitEvent: true }
    );

    this.syncScopeControl(priceRegulation.scopeType);
    this.syncTypeControls();
    this.form.markAsPristine();
  }

  private syncSupplementControl(control: FormControl<number | null>, enabled: boolean): void {
    if (enabled) {
      control.enable({ emitEvent: false });
      control.setValidators([Validators.required, Validators.min(0), Validators.max(100)]);
    } else {
      control.setValue(null, { emitEvent: false });
      control.clearValidators();
      control.disable({ emitEvent: false });
    }

    control.updateValueAndValidity({ emitEvent: false });
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  private syncTypeControls(): void {
    if (this.form.controls.type.value === PriceRegulationType.Discount) {
      this.form.controls.discountAmountInKrWithoutVat.enable({ emitEvent: false });
      this.form.controls.discountAmountInKrWithoutVat.setValidators([Validators.required, Validators.min(0), Validators.max(100)]);
      this.form.controls.discountAmountInKrWithoutVat.updateValueAndValidity({ emitEvent: false });

      this.form.controls.overrideOkSupplement.disable({ emitEvent: false });
      this.form.controls.overrideOrganizationSupplement.disable({ emitEvent: false });

      this.form.controls.okSupplementOverrideInKrWithoutVat.disable({ emitEvent: false });
      this.form.controls.organizationSupplementOverrideInKrWithoutVat.disable({ emitEvent: false });

      this.form.updateValueAndValidity({ emitEvent: false });
      return;
    }

    this.form.controls.discountAmountInKrWithoutVat.setValue(null, { emitEvent: false });
    this.form.controls.discountAmountInKrWithoutVat.clearValidators();
    this.form.controls.discountAmountInKrWithoutVat.disable({ emitEvent: false });

    this.form.controls.overrideOkSupplement.enable({ emitEvent: false });
    this.form.controls.overrideOrganizationSupplement.enable({ emitEvent: false });

    this.syncSupplementControl(this.form.controls.okSupplementOverrideInKrWithoutVat, this.form.controls.overrideOkSupplement.value);
    this.syncSupplementControl(
      this.form.controls.organizationSupplementOverrideInKrWithoutVat,
      this.form.controls.overrideOrganizationSupplement.value
    );
  }

  public selectOrganization(org: Organization | null): void {
    this.form.controls.organization.setValue(org ? { id: org.id, name: org.name } : null);
    this.form.controls.organization.markAsDirty();
  }

  public resetOrganization(): void {
    this.form.controls.organization.setValue(null);
    this.form.controls.organization.markAsDirty();
  }

  public cancel(): void {
    this.dialogRef?.close();
  }

  public save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.isEditMode) {
      const noteDialogRef = this.dialog.open(NoteCreationModalComponent, {
        data: {
          title: 'Tilføj note omkring redigering',
          confirmText: 'Gem',
          denyText: 'Luk',
          icon: { name: 'edit-s' },
        },
      });

      noteDialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe(noteDialogResult => {
          if (!noteDialogResult?.trim?.()) {
            return;
          }
          this.executeSave(noteDialogResult);
        });
      return;
    }

    this.executeSave();
  }

  private executeSave(note?: string): void {
    const request$ = this.buildSaveRequest(note);
    if (!request$) {
      return;
    }

    request$.pipe(take(1)).subscribe(identifier => {
      const regulationIdentifier = this.isEditMode ? (this.data?.identifier ?? identifier) : identifier;
      const formValue = this.form.getRawValue();
      this.dialogRef?.close({
        saved: true,
        sortBy: this.isEditMode ? 'updated' : 'created',
        identifier: regulationIdentifier,
        ...(!this.isEditMode
          ? {
              priceRegulation: {
                identifier: regulationIdentifier,
                name: formValue.name,
                scopeType: formValue.scopeType,
                organizationId: formValue.organization?.id ?? null,
                organizationName: formValue.organization?.name ?? null,
                type: formValue.type,
                okSupplementOverrideInKrWithoutVat: formValue.overrideOkSupplement ? formValue.okSupplementOverrideInKrWithoutVat : null,
                organizationSupplementOverrideInKrWithoutVat: formValue.overrideOrganizationSupplement
                  ? formValue.organizationSupplementOverrideInKrWithoutVat
                  : null,
                discountAmountInKrWithoutVat:
                  formValue.type === PriceRegulationType.Discount ? formValue.discountAmountInKrWithoutVat : null,
                locationCount: null,
              },
            }
          : {}),
      } satisfies PriceRegulationDialogCloseResult);
    });
  }

  private buildSaveRequest(note?: string): Observable<string> | null {
    const formValue = this.form.getRawValue();

    if (formValue.type === PriceRegulationType.Discount) {
      const discountValue = formValue.discountAmountInKrWithoutVat;
      if (discountValue == null) {
        this.form.controls.discountAmountInKrWithoutVat.markAsTouched();
        return null;
      }

      if (this.isEditMode) {
        if (!note) {
          return null;
        }
        const request = this.buildEditDiscountPriceRegulationRequest(formValue.name, discountValue, note);
        return this.priceRegulationService.updatePriceRegulation(this.data!.identifier!, request);
      }

      const request = this.buildCreatePriceRegulationRequest();
      if (!request) {
        this.form.markAllAsTouched();
        return null;
      }

      return this.priceRegulationService.createPriceRegulation(request);
    }

    const okSupplementValue = formValue.overrideOkSupplement ? formValue.okSupplementOverrideInKrWithoutVat : null;

    const organizationSupplementValue = formValue.overrideOrganizationSupplement
      ? formValue.organizationSupplementOverrideInKrWithoutVat
      : null;

    if (okSupplementValue == null && organizationSupplementValue == null) {
      this.form.markAllAsTouched();
      return null;
    }

    const supplementRequest = {
      name: formValue.name,
      note: this.isEditMode ? (note ?? null) : null,
      ok_supplement_override_in_kr_without_vat: formValue.overrideOkSupplement ? formValue.okSupplementOverrideInKrWithoutVat : null,
      organization_supplement_override_in_kr_without_vat: formValue.overrideOrganizationSupplement
        ? formValue.organizationSupplementOverrideInKrWithoutVat
        : null,
      ...(this.isEditMode ? {} : { organization_id: formValue.organization?.id ?? null }),
    };

    if (!this.isEditMode) {
      const request = this.buildCreatePriceRegulationRequest();

      if (!request) {
        this.form.markAllAsTouched();
        return null;
      }

      return this.priceRegulationService.createPriceRegulation(request);
    }

    if (!formValue.overrideOkSupplement && !formValue.overrideOrganizationSupplement) {
      this.form.markAllAsTouched();
      return null;
    }

    return this.priceRegulationService.updateSupplementOverridePriceRegulation(
      this.data!.identifier!,
      supplementRequest as EditSupplementOverridePriceRegulationRequest
    );
  }

  private buildCreatePriceRegulationRequest(): CreatePriceRegulationRequest | null {
    const formValue = this.form.getRawValue();

    const isDiscount = formValue.type === PriceRegulationType.Discount;

    const request: CreatePriceRegulationRequest = {
      name: formValue.name,
      organization_id: formValue.organization?.id ?? null,
      type: formValue.type,
      discount_amount_in_kr_without_vat: isDiscount ? formValue.discountAmountInKrWithoutVat : null,
      ok_supplement_override_in_kr_without_vat: isDiscount
        ? null
        : formValue.overrideOkSupplement
          ? formValue.okSupplementOverrideInKrWithoutVat
          : null,
      organization_supplement_override_in_kr_without_vat: isDiscount
        ? null
        : formValue.overrideOrganizationSupplement
          ? formValue.organizationSupplementOverrideInKrWithoutVat
          : null,
      note: null,
    };

    if (
      formValue.type === PriceRegulationType.SupplementOverride &&
      request.ok_supplement_override_in_kr_without_vat === null &&
      request.organization_supplement_override_in_kr_without_vat === null
    ) {
      return null;
    }

    return request;
  }

  private buildEditDiscountPriceRegulationRequest(
    name: string,
    discountAmountInKrWithoutVat: number,
    note: string
  ): EditDiscountPriceRegulationRequest {
    return {
      name,
      discount_amount_in_kr_without_vat: discountAmountInKrWithoutVat,
      note,
    };
  }
}
